import { useEffect, useRef, useState } from 'react';
import { Icon } from './ui/Icon';
import type { Asset } from '@/lib/api';

type Props = {
  asset: Asset;
};

function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function VideoPreview({ asset }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  // Build the URL through Worker /api/media so user JWT validates access.
  // We strip the topstudio/{user-id}/ prefix because the worker re-prefixes per request.
  const r2Path = asset.r2_key.replace(/^topstudio\/[^/]+\//, '');
  const src = `/api/media/${encodeURIComponent(r2Path)}`;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrent(v.currentTime);
    const onMeta = () => setDuration(v.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [asset.id]);

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = Math.max(0, Math.min(duration, pct * duration));
  }

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div className="flex flex-col gap-3.5 h-full">
      <div className="flex-1 bg-black rounded-xl overflow-hidden relative shadow-2xl flex items-center justify-center">
        {asset.kind === 'video' && (
          <video
            ref={videoRef}
            src={src}
            className="w-full h-full object-contain"
            playsInline
            preload="metadata"
          />
        )}
        {asset.kind === 'image' && (
          <img src={src} alt={asset.original_name ?? ''} className="max-w-full max-h-full" />
        )}
        {asset.kind === 'audio' && (
          <div className="text-text-muted flex flex-col items-center gap-3 p-8">
            <Icon name="music" size={48} />
            <audio ref={videoRef as React.RefObject<HTMLAudioElement>} src={src} controls />
          </div>
        )}
      </div>

      <div className="bg-bg-panel border border-border rounded-[10px] px-3.5 py-2 flex items-center gap-3.5">
        <button
          onClick={togglePlay}
          className="w-[34px] h-[34px] rounded-[7px] bg-bg-elevated border border-border hover:bg-accent hover:border-accent flex items-center justify-center text-text transition-all"
        >
          <Icon name={playing ? 'pause' : 'play'} size={14} />
        </button>
        <div className="font-mono text-xs text-text-muted font-medium">{formatTime(current)}</div>
        <div
          className="flex-1 h-[5px] bg-bg-elevated rounded-[3px] relative cursor-pointer"
          onClick={seek}
        >
          <div
            className="absolute left-0 top-0 bottom-0 rounded-[3px] bg-gradient-to-r from-accent to-ai"
            style={{ width: `${pct}%` }}
          />
          <div
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-[0_2px_10px_rgba(59,130,246,0.5)]"
            style={{ left: `${pct}%` }}
          />
        </div>
        <div className="font-mono text-xs text-text-muted font-medium">{formatTime(duration)}</div>
      </div>
    </div>
  );
}
